import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Shield, Users, CreditCard, TrendingUp, Heart, MessageSquare, 
  Crown, LogOut, ChevronRight, Search, Filter, Eye, Calendar,
  DollarSign, UserCheck, UserX, Zap, BarChart3, ArrowUpRight,
  ArrowDownRight, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Admin Auth Hook
const useAdminAuth = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('adminToken');
  
  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
    }
  }, [token, navigate]);
  
  const logout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };
  
  return { token, logout };
};

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, trend, trendUp, color = "primary" }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-slate-800/50 backdrop-blur rounded-xl border border-slate-700/50 p-5"
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-slate-400 text-sm">{title}</p>
        <p className="text-2xl font-bold text-white mt-1">{value}</p>
        {trend && (
          <div className={`flex items-center gap-1 mt-2 text-xs ${trendUp ? 'text-emerald-400' : 'text-red-400'}`}>
            {trendUp ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            <span>{trend}</span>
          </div>
        )}
      </div>
      <div className={`w-10 h-10 rounded-lg bg-${color}/20 flex items-center justify-center`}>
        <Icon className={`w-5 h-5 text-${color}`} />
      </div>
    </div>
  </motion.div>
);

// Admin Dashboard Page
const AdminDashboard = () => {
  const { token, logout } = useAdminAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/api/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStats(response.data);
      } catch (err) {
        console.error('Failed to fetch stats:', err);
        if (err.response?.status === 401 || err.response?.status === 403) {
          logout();
        }
      } finally {
        setLoading(false);
      }
    };

    if (token) fetchStats();
  }, [token, logout]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-800/50 backdrop-blur-xl border-b border-slate-700/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-primary to-accent rounded-xl flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Flirra Admin</h1>
              <p className="text-xs text-slate-400">Dashboard</p>
            </div>
          </div>
          
          <nav className="flex items-center gap-2">
            <Link to="/admin">
              <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white">
                <BarChart3 className="w-4 h-4 mr-2" />
                Oversigt
              </Button>
            </Link>
            <Link to="/admin/users">
              <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white">
                <Users className="w-4 h-4 mr-2" />
                Brugere
              </Button>
            </Link>
            <Link to="/admin/payments">
              <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white">
                <CreditCard className="w-4 h-4 mr-2" />
                Betalinger
              </Button>
            </Link>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={logout}
              className="text-slate-400 hover:text-red-400 ml-4"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            title="Totale Brugere"
            value={stats?.total_users || 0}
            icon={Users}
            trend={`+${stats?.weekly_growth || 0} denne uge`}
            trendUp={true}
          />
          <StatCard
            title="Premium Medlemmer"
            value={stats?.premium_users || 0}
            icon={Crown}
            color="amber"
          />
          <StatCard
            title="Total Omsætning"
            value={`${stats?.total_revenue || 0} DKK`}
            icon={DollarSign}
            trend={`${stats?.monthly_revenue || 0} DKK denne måned`}
            trendUp={true}
            color="emerald"
          />
          <StatCard
            title="Matches"
            value={stats?.total_matches || 0}
            icon={Heart}
            color="pink"
          />
        </div>

        {/* Secondary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-slate-800/50 backdrop-blur rounded-xl border border-slate-700/50 p-5"
          >
            <h3 className="text-slate-400 text-sm mb-4">Premium Fordeling</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-slate-300">Basis</span>
                <Badge className="bg-blue-500/20 text-blue-400 border-0">
                  {stats?.premium_breakdown?.basic || 0}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-300">Guld</span>
                <Badge className="bg-amber-500/20 text-amber-400 border-0">
                  {stats?.premium_breakdown?.gold || 0}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-300">Platin</span>
                <Badge className="bg-purple-500/20 text-purple-400 border-0">
                  {stats?.premium_breakdown?.platinum || 0}
                </Badge>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-slate-800/50 backdrop-blur rounded-xl border border-slate-700/50 p-5"
          >
            <h3 className="text-slate-400 text-sm mb-4">Bruger Status</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-slate-300 flex items-center gap-2">
                  <UserCheck className="w-4 h-4 text-emerald-400" />
                  Premium
                </span>
                <span className="text-white font-medium">{stats?.premium_users || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-300 flex items-center gap-2">
                  <UserX className="w-4 h-4 text-slate-400" />
                  Gratis
                </span>
                <span className="text-white font-medium">{(stats?.total_users || 0) - (stats?.premium_users || 0)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-300 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-amber-400" />
                  Prøvet Trial
                </span>
                <span className="text-white font-medium">{stats?.trial_users || 0}</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-slate-800/50 backdrop-blur rounded-xl border border-slate-700/50 p-5"
          >
            <h3 className="text-slate-400 text-sm mb-4">Engagement</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-slate-300 flex items-center gap-2">
                  <Heart className="w-4 h-4 text-pink-400" />
                  Matches
                </span>
                <span className="text-white font-medium">{stats?.total_matches || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-300 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-blue-400" />
                  Beskeder
                </span>
                <span className="text-white font-medium">{stats?.total_messages || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-300 flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-emerald-400" />
                  Transaktioner
                </span>
                <span className="text-white font-medium">{stats?.total_transactions || 0}</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link to="/admin/users">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="bg-slate-800/50 backdrop-blur rounded-xl border border-slate-700/50 p-5 hover:bg-slate-800/70 transition-colors group"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                    <Users className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium">Se alle brugere</h3>
                    <p className="text-slate-400 text-sm">{stats?.total_users || 0} registrerede brugere</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-500 group-hover:text-white transition-colors" />
              </div>
            </motion.div>
          </Link>

          <Link to="/admin/payments">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-slate-800/50 backdrop-blur rounded-xl border border-slate-700/50 p-5 hover:bg-slate-800/70 transition-colors group"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="text-white font-medium">Se alle betalinger</h3>
                    <p className="text-slate-400 text-sm">{stats?.total_revenue || 0} DKK total omsætning</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-500 group-hover:text-white transition-colors" />
              </div>
            </motion.div>
          </Link>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
